/**
 * Global Error Handlers
 *
 * Capture uncaught errors and unhandled promise rejections.
 * Reports via the observability adapter without preventing default handling.
 */

import { captureException, captureMessage } from "./adapter"

let installed = false

/**
 * Install global error handlers.
 * Safe to call multiple times - will only install once.
 */
export function installGlobalErrorHandlers(): void {
  if (installed) return
  installed = true

  // Capture uncaught errors
  window.addEventListener("error", (event) => {
    // Don't report resource loading errors (scripts, images, etc.)
    if (event.target && (event.target as HTMLElement).tagName) {
      return
    }

    if (event.error instanceof Error) {
      captureException(event.error, {
        type: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    } else {
      captureMessage(event.message || "Unknown error", "error", {
        type: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    }

    // Don't prevent default handling
  })

  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason

    if (reason instanceof Error) {
      captureException(reason, {
        type: "unhandledrejection",
      })
    } else if (typeof reason === "string") {
      captureMessage(`Unhandled rejection: ${reason}`, "error", {
        type: "unhandledrejection",
      })
    } else {
      captureMessage("Unhandled promise rejection", "error", {
        type: "unhandledrejection",
        reason: String(reason),
      })
    }

    // Don't prevent default handling
  })

  if (import.meta.env.DEV) {
    console.log("[observability] Global error handlers installed")
  }
}
